import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SessionComment {
  id: string;
  session_id: string;
  user_id: string;
  content: string;
  mentioned_users: string[];
  created_at: string;
  updated_at: string;
  profiles?: {
    name: string | null;
    avatar_url: string | null;
  };
}

export function useSessionComments(sessionId: string | undefined) {
  const [comments, setComments] = useState<SessionComment[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch initial comments
  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }

    async function fetchComments() {
      try {
        const { data, error } = await supabase
          .from('session_comments')
          .select(`
            *,
            profiles:user_id (
              name,
              avatar_url
            )
          `)
          .eq('session_id', sessionId)
          .order('created_at', { ascending: true });

        if (error) throw error;

        setComments(data || []);
      } catch (error) {
        console.error('Error fetching comments:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchComments();
  }, [sessionId]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase
      .channel(`session-comments:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'session_comments',
          filter: `session_id=eq.${sessionId}`,
        },
        async (payload) => {
          // Fetch the complete comment with profile data
          const { data } = await supabase
            .from('session_comments')
            .select(`
              *,
              profiles:user_id (
                name,
                avatar_url
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (data) {
            setComments((prev) => [...prev, data]);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'session_comments',
          filter: `session_id=eq.${sessionId}`,
        },
        async (payload) => {
          // Fetch the complete comment with profile data
          const { data } = await supabase
            .from('session_comments')
            .select(`
              *,
              profiles:user_id (
                name,
                avatar_url
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (data) {
            setComments((prev) =>
              prev.map((c) => (c.id === data.id ? data : c))
            );
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'session_comments',
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          setComments((prev) => prev.filter((c) => c.id !== payload.old.id));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId]);

  const addComment = async (content: string, mentionedUsers: string[]) => {
    if (!sessionId) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Optimistic update - add comment immediately to UI
      const optimisticComment: SessionComment = {
        id: `temp-${Date.now()}`,
        session_id: sessionId,
        user_id: user.id,
        content,
        mentioned_users: mentionedUsers,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        profiles: undefined, // Will be filled by realtime update
      };
      
      setComments(prev => [...prev, optimisticComment]);

      const { data, error } = await supabase
        .from('session_comments')
        .insert({
          session_id: sessionId,
          user_id: user.id,
          content,
          mentioned_users: mentionedUsers,
        })
        .select(`
          *,
          profiles:user_id (
            name,
            avatar_url
          )
        `)
        .single();

      if (error) {
        // Remove optimistic comment on error
        setComments(prev => prev.filter(c => c.id !== optimisticComment.id));
        throw error;
      }

      // Replace optimistic comment with real one
      if (data) {
        setComments(prev => 
          prev.map(c => c.id === optimisticComment.id ? data : c)
        );
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  };

  const deleteComment = async (commentId: string) => {
    try {
      const { error } = await supabase
        .from('session_comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  };

  return {
    comments,
    loading,
    addComment,
    deleteComment,
  };
}
